import * as React from "react";
import * as ToastPrimitive from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import {
  RiInformationLine,
  RiCheckboxCircleLine,
  RiAlertLine,
  RiCloseCircleLine,
  RiCloseLine,
} from "@remixicon/react";
import { cn } from "../../../lib/utils";
import {
  ToastContextProvider,
  setToastListeners,
  clearToastListeners,
  generateId,
  type ToastData,
  type ToastVariant,
  type ToastAction,
  type ToastLink,
} from "./useToast";

// Re-export everything from useToast
export * from "./useToast";

// =============================================================================
// CVA Variants
// =============================================================================

const toastVariants = cva(
  [
    "group pointer-events-auto relative flex w-full gap-2 overflow-hidden rounded-lg border p-4",
    "shadow-[0_12px_16px_-4px_rgba(2,0,94,0.08),0_4px_6px_-2px_rgba(2,0,94,0.03)]",
    "data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)]",
    "data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none",
    "data-[state=open]:animate-in data-[state=closed]:animate-out",
    "data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full",
    "data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full",
  ],
  {
    variants: {
      variant: {
        info: "border-stroke-blue bg-bg-primary",
        success: "border-stroke-success bg-bg-success",
        warning: "border-stroke-warning bg-bg-warning",
        critical: "border-stroke-critical bg-bg-critical",
      },
      // Controls vertical alignment based on content complexity
      contentType: {
        simple: "items-center", // Single line - center everything
        complex: "items-start", // Multi-line or with actions - align to top
      },
    },
    defaultVariants: {
      variant: "info",
      contentType: "simple",
    },
  },
);

const iconVariants = cva("h-6 w-6 shrink-0", {
  variants: {
    variant: {
      info: "text-icon-blue",
      success: "text-icon-success",
      warning: "text-icon-warning",
      critical: "text-icon-critical",
    },
  },
  defaultVariants: {
    variant: "info",
  },
});

// =============================================================================
// Default Icons
// =============================================================================

const defaultIcons: Record<ToastVariant, React.ReactNode> = {
  info: <RiInformationLine />,
  success: <RiCheckboxCircleLine />,
  warning: <RiAlertLine />,
  critical: <RiCloseCircleLine />,
};

// =============================================================================
// ToastProvider
// =============================================================================

export interface ToastProviderProps {
  children?: React.ReactNode;
  /** Default duration in milliseconds. Default: 5000 */
  defaultDuration?: number;
  /** Maximum number of toasts to show at once. Default: 3 */
  maxToasts?: number;
}

/**
 * ToastProvider - Wraps the application to provide toast functionality
 *
 * Must be placed at the root of your app, typically in App.tsx
 *
 * @example
 * ```tsx
 * import { ToastProvider, ToastViewport } from '@pfizer/shared/components';
 *
 * function App() {
 *   return (
 *     <ToastProvider>
 *       <YourApp />
 *       <ToastViewport />
 *     </ToastProvider>
 *   );
 * }
 * ```
 */
export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  defaultDuration = 5000,
  maxToasts = 3,
}) => {
  const [toasts, setToasts] = React.useState<ToastData[]>([]);

  const addToast = React.useCallback(
    (toast: Omit<ToastData, "id">) => {
      const id = generateId();
      const newToast: ToastData = {
        ...toast,
        id,
        duration: toast.duration ?? defaultDuration,
      };

      setToasts((prev) => {
        // Limit the number of toasts
        const updated = [...prev, newToast];
        if (updated.length > maxToasts) {
          return updated.slice(-maxToasts);
        }
        return updated;
      });

      return id;
    },
    [defaultDuration, maxToasts],
  );

  const removeToast = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const removeAllToasts = React.useCallback(() => {
    setToasts([]);
  }, []);

  // Set up listeners for imperative API
  React.useEffect(() => {
    setToastListeners(addToast, (id) => {
      if (id) {
        removeToast(id);
      } else {
        removeAllToasts();
      }
    });

    return () => {
      clearToastListeners();
    };
  }, [addToast, removeToast, removeAllToasts]);

  return (
    <ToastPrimitive.Provider swipeDirection="right">
      <ToastContextProvider
        value={{ toasts, addToast, removeToast, removeAllToasts }}
      >
        {children}
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            id={toast.id}
            variant={toast.variant}
            alignment={toast.alignment}
            title={toast.title}
            description={toast.description}
            icon={toast.icon}
            hideIcon={toast.hideIcon}
            closable={toast.closable}
            duration={toast.duration}
            action={toast.action}
            link={toast.link}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </ToastContextProvider>
    </ToastPrimitive.Provider>
  );
};

// =============================================================================
// ToastViewport
// =============================================================================

export interface ToastViewportProps {
  /** Position of the toast viewport. Default: 'bottom-right' */
  position?:
    | "top-left"
    | "top-center"
    | "top-right"
    | "bottom-left"
    | "bottom-center"
    | "bottom-right";
  className?: string;
}

const positionClasses: Record<
  NonNullable<ToastViewportProps["position"]>,
  string
> = {
  "top-left": "top-0 left-0",
  "top-center": "top-0 left-1/2 -translate-x-1/2",
  "top-right": "top-0 right-0",
  "bottom-left": "bottom-0 left-0",
  "bottom-center": "bottom-0 left-1/2 -translate-x-1/2",
  "bottom-right": "bottom-0 right-0",
};

/**
 * ToastViewport - Container where toasts are rendered
 *
 * Place this at the end of your app, inside ToastProvider
 *
 * @example
 * ```tsx
 * <ToastProvider>
 *   <App />
 *   <ToastViewport position="bottom-right" />
 * </ToastProvider>
 * ```
 */
export const ToastViewport: React.FC<ToastViewportProps> = ({
  position = "bottom-right",
  className,
}) => {
  return (
    <ToastPrimitive.Viewport
      className={cn(
        "fixed z-[100] flex max-h-screen w-full flex-col-reverse gap-2 p-4 sm:w-[458px]",
        positionClasses[position],
        className,
      )}
    />
  );
};

// =============================================================================
// Toast
// =============================================================================

export interface ToastProps extends VariantProps<typeof toastVariants> {
  id: string;
  /** Button alignment: horizontal (beside content) or vertical (below content) */
  alignment?: "horizontal" | "vertical";
  title?: string;
  description: string;
  icon?: React.ReactNode;
  hideIcon?: boolean;
  closable?: boolean;
  onClose?: () => void;
  duration?: number;
  action?: ToastAction;
  link?: ToastLink;
  className?: string;
}

/**
 * Toast - Individual toast notification component
 *
 * Usually you don't need to use this directly - use the `toast` API instead.
 * This component is used internally by ToastProvider.
 */
export const Toast: React.FC<ToastProps> = ({
  id,
  variant = "info",
  alignment = "horizontal",
  title,
  description,
  icon,
  hideIcon = false,
  closable = true,
  onClose,
  duration,
  action,
  link,
  className,
}) => {
  const displayIcon = icon ?? defaultIcons[variant ?? "info"];
  const isVertical = alignment === "vertical";
  // Determine if content is complex (has title or actions) - use top alignment
  // Simple content (just description) - use center alignment
  const isComplex = Boolean(title || action || link);
  const contentType = isComplex ? "complex" : "simple";

  return (
    <ToastPrimitive.Root
      key={id}
      duration={duration}
      onOpenChange={(open) => {
        if (!open) onClose?.();
      }}
      className={cn(toastVariants({ variant, contentType }), className)}
    >
      {/* Icon */}
      {!hideIcon && (
        <div className={cn(iconVariants({ variant }))}>{displayIcon}</div>
      )}

      {/* Content */}
      <div className="flex flex-1 flex-col">
        {title && (
          <ToastPrimitive.Title className="font-diatype text-base font-bold leading-6 text-text-primary">
            {title}
          </ToastPrimitive.Title>
        )}
        <ToastPrimitive.Description className="font-diatype text-base leading-5 text-text-primary">
          {description}
        </ToastPrimitive.Description>

        {/* Actions for vertical alignment - buttons below content */}
        {isVertical && (action || link) && (
          <div className="mt-2 flex items-center gap-3">
            {action && (
              <button
                type="button"
                onClick={action.onClick}
                className={cn(
                  "inline-flex h-8 items-center justify-center rounded-lg border px-3",
                  "text-sm font-medium text-text-primary",
                  "border-stroke-primary hover:bg-bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stroke-blue",
                )}
              >
                {action.label}
              </button>
            )}
            {link && <ToastLink link={link} />}
          </div>
        )}
      </div>

      {/* Actions for horizontal alignment - buttons beside content */}
      {!isVertical && (action || link) && (
        <div className="flex shrink-0 items-center gap-3">
          {action && (
            <button
              type="button"
              onClick={action.onClick}
              className={cn(
                "inline-flex h-8 items-center justify-center rounded-lg border px-3",
                "text-sm font-medium text-text-primary",
                "border-stroke-primary hover:bg-bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stroke-blue",
              )}
            >
              {action.label}
            </button>
          )}
          {link && <ToastLink link={link} />}
        </div>
      )}

      {/* Close button - uses flexbox alignment */}
      {closable && (
        <ToastPrimitive.Close
          className={cn(
            "shrink-0 rounded-md p-1",
            "text-text-primary",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stroke-blue",
            // For complex content, align to top
            isComplex && "self-start",
          )}
          aria-label="Close"
        >
          <RiCloseLine className="h-6 w-6" />
        </ToastPrimitive.Close>
      )}
    </ToastPrimitive.Root>
  );
};

// =============================================================================
// ToastLink - Internal component for rendering links
// =============================================================================

interface ToastLinkProps {
  link: ToastLink;
}

const ToastLink: React.FC<ToastLinkProps> = ({ link }) => {
  const baseClasses = cn(
    "text-sm font-medium text-text-link",
    "hover:text-text-link-hover hover:underline",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stroke-blue",
  );

  if (link.href) {
    return (
      <a href={link.href} className={baseClasses} onClick={link.onClick}>
        {link.label}
      </a>
    );
  }

  return (
    <button type="button" onClick={link.onClick} className={baseClasses}>
      {link.label}
    </button>
  );
};

// =============================================================================
// Toaster - Convenience component that combines Provider and Viewport
// =============================================================================

export interface ToasterProps extends ToastProviderProps, ToastViewportProps {}

/**
 * Toaster - Convenience component that combines ToastProvider and ToastViewport
 *
 * Use this if you want a simpler setup. Place it at the end of your app.
 *
 * @example
 * ```tsx
 * import { Toaster, toast } from '@pfizer/shared/components';
 *
 * function App() {
 *   return (
 *     <>
 *       <YourApp />
 *       <Toaster position="bottom-center" />
 *     </>
 *   );
 * }
 *
 * // Anywhere in your app:
 * toast.success('Operation completed!');
 * ```
 */
export const Toaster: React.FC<ToasterProps> = ({
  children,
  defaultDuration,
  maxToasts,
  position,
  className,
}) => {
  return (
    <ToastProvider defaultDuration={defaultDuration} maxToasts={maxToasts}>
      {children}
      <ToastViewport position={position} className={className} />
    </ToastProvider>
  );
};
